import React, { useState } from 'react';

const RED = '#bf1e2e';
const SURFACE = '#0d1525';
const BORDER = 'rgba(255,255,255,0.08)';
const DIM = 'rgba(255,255,255,0.45)';
const SUBJECTS = ['General Inquiry', 'Institutional Partnership', 'Press / Media', 'Other'];
const ENDPOINT = 'https://ieszywnmoenxmrlaymtl.supabase.co/functions/v1/contact-form';

interface Props { onClose: () => void; }

export default function ContactModal({ onClose }: Props) {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.subject) { setError('Please select a subject.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', background: 'rgba(255,255,255,0.05)',
    border: `1px solid ${BORDER}`, borderRadius: 8,
    padding: '12px 14px', color: '#fff', fontSize: 14,
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 800,
    color: DIM, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 8,
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: SURFACE, border: `1px solid ${BORDER}`,
          borderRadius: 16, padding: 40, maxWidth: 520, width: '100%',
          position: 'relative', maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 16, right: 16,
            background: 'none', border: 'none',
            color: 'rgba(255,255,255,0.4)', fontSize: 22,
            cursor: 'pointer', lineHeight: 1, padding: '0 4px',
          }}
        >×</button>

        {submitted ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'rgba(15,168,109,0.15)', border: '2px solid rgba(15,168,109,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 24px', fontSize: 26,
            }}>✓</div>
            <h2 style={{ color: '#fff', fontSize: 22, fontWeight: 900, marginBottom: 12 }}>Message Received</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 15, lineHeight: 1.6, marginBottom: 28 }}>
              We'll get back to you within one business day.
            </p>
            <button
              onClick={onClose}
              style={{
                background: RED, color: '#fff', border: 'none',
                borderRadius: 8, padding: '12px 28px',
                fontSize: 14, fontWeight: 800, cursor: 'pointer',
              }}
            >Close</button>
          </div>
        ) : (
          <>
            <p style={{ fontSize: 11, fontWeight: 800, color: RED, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 8 }}>GET IN TOUCH</p>
            <h2 style={{ color: '#fff', fontSize: 32, fontWeight: 900, marginBottom: 24, fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase', letterSpacing: 0.5 }}>Contact BTHD</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }} className="cm-grid">
                <div>
                  <label style={labelStyle}>Name *</label>
                  <input type="text" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your full name" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Email *</label>
                  <input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="your@email.com" style={inputStyle} />
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Subject *</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {SUBJECTS.map(s => {
                    const active = form.subject === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, subject: s }))}
                        style={{
                          padding: '9px 16px', borderRadius: 20, cursor: 'pointer',
                          border: active ? `1.5px solid ${RED}` : '1.5px solid rgba(255,255,255,0.12)',
                          background: active ? RED : 'rgba(255,255,255,0.04)',
                          color: active ? '#fff' : 'rgba(255,255,255,0.55)',
                          fontSize: 13, fontWeight: active ? 700 : 500,
                          fontFamily: 'inherit', transition: 'all 0.15s',
                          whiteSpace: 'nowrap',
                        }}
                      >{s}</button>
                    );
                  })}
                </div>
              </div>
              <div style={{ marginBottom: 28 }}>
                <label style={labelStyle}>Message *</label>
                <textarea
                  required value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  placeholder="How can we help?"
                  rows={5}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </div>
              {error && <p style={{ color: RED, fontSize: 13, marginBottom: 16 }}>{error}</p>}
              <button
                type="submit" disabled={submitting}
                style={{
                  width: '100%', background: submitting ? 'rgba(191,30,46,0.5)' : RED,
                  color: '#fff', border: 'none', borderRadius: 8, padding: '16px',
                  fontSize: 14, fontWeight: 800, letterSpacing: 0.5,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? 'Sending...' : 'SEND MESSAGE'}
              </button>
            </form>
          </>
        )}
        <style>{`@media(max-width:540px){.cm-grid{grid-template-columns:1fr!important}}`}</style>
      </div>
    </div>
  );
}
